const isEightDigits = v => /^\d{8}$/.test(v);
const isEmptyOrWhitespace = el => !el || !el.value || el.value.trim().length === 0;

const silencedInputs = new WeakSet();

const setIfEmpty = (el, value) =>
{
    if (!el)
        return;

    if (!isEmptyOrWhitespace(el))
        return; // do not overwrite user-entered values

    if (value == null)
        return;

    el.value = value;

    // Fire input event in case any validators/listeners need it
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
};

// Set value and optionally silence 'input' handlers (dispatch only 'change')
const setValue = (el, value, silent = false) =>
{
    if (!el)
        return;

    if (silent)
    {
        silencedInputs.add(el);

        try
        {
            el.value = value ?? "";

            el.dispatchEvent(new Event('change', { bubbles: true }));
        }
        finally
        {
            // Let event loop finish before re-enabling input handlers
            setTimeout(() => silencedInputs.delete(el), 0);
        }

        return;
    }

    el.value = value ?? "";
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
};

const forceSet = (el, value) =>
{
    if (!el)
        return;

    el.value = value ?? "";

    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
};

const fetchAresAsync = async (ico, countryCode = "CZ") =>
{
    const key = `${countryCode}:${ico}`;

    if (clientCache.has(key))
        return clientCache.get(key);

    let data = null;

    try
    {
        const response = await fetch(`${document.baseURI}api/v1/ares/ico/${ico}?countryCode=${encodeURIComponent(countryCode)}`,
        {
            headers:
            {
                'Accept': 'application/json'
            }
        });

        if (!response.ok)
            return null;

        data = await response.json();
    }
    catch (error)
    {
        console.log(error);
    }

    if (data == null)
        return null;

    clientCache.set(key, data);

    return data;
};

const fetchAresByNameAsync = async (query, countryCode = "CZ") =>
{
    const key = `q:${countryCode}:${query}`;

    if (clientCache.has(key))
        return clientCache.get(key);

    let data = null;

    try
    {
        const url = `${document.baseURI}api/v1/ares/name/${encodeURIComponent(query)}?countryCode=${encodeURIComponent(countryCode)}`;
        const response = await fetch(url,
        {
            headers:
            {
                'Accept': 'application/json'
            }
        });

        if (!response.ok)
            return [];

        data = await response.json();
    }
    catch (error)
    {
        console.log(error);
    }

    data = Array.isArray(data)
        ? data
        : [];

    clientCache.set(key, data);

    return data;
};

const clientCache = new Map(); // client-side cache by ICO

const resolveCountryCode = (countryElement) =>
{
    if (!countryElement || !countryElement.options || countryElement.selectedIndex < 0)
        return "CZ";

    const selected = countryElement.options[countryElement.selectedIndex];

    if (!selected)
        return "CZ";

    const optionCode = (selected.dataset && selected.dataset.code)
        ? selected.dataset.code
        : "";
    const normalizedOptionCode = optionCode.trim().toUpperCase();

    if (normalizedOptionCode === "SK")
        return "SK";

    return "CZ";
};

window.ares =
{
    bind: function (input)
    {
        let timer = null;
        let lastRequestedIco = null;

        const dataSet = input.dataset;

        // Resolve targets: prefer explicit selectors, fallback to common IDs
        const companyElement = dataSet.aresCompanySelector
            ? document.querySelector(dataSet.aresCompanySelector)
            : document.getElementById("billing-company");

        const vatElement = dataSet.aresVatSelector
            ? document.querySelector(dataSet.aresVatSelector)
            : document.getElementById("billing-dic");

        const addressElement = dataSet.aresAddressSelector
            ? document.querySelector(dataSet.aresAddressSelector)
            : document.getElementById("billing-address-1");

        const cityElement = dataSet.aresCitySelector
            ? document.querySelector(dataSet.aresCitySelector)
            : document.getElementById("billing-city");

        const postCodeElement = dataSet.aresPostcodeSelector
            ? document.querySelector(dataSet.aresPostcodeSelector)
            : document.getElementById("billing-zip");

        const taxElement = dataSet.aresTaxSelector
            ? document.querySelector(dataSet.aresTaxSelector)
            : document.getElementById("billing-icd");

        const countryElement = dataSet.aresCountrySelector
            ? document.querySelector(dataSet.aresCountrySelector)
            : document.getElementById("billing-country");

        const handle = async () =>
        {
            const ico = (input.value || "").replace(/\s+/g, "");

            if (!isEightDigits(ico))
                return;

            lastRequestedIco = ico;

            const countryCode = resolveCountryCode(countryElement);
            const data = await fetchAresAsync(ico, countryCode);

            // Ignore late responses if user changed the ICO
            if ((input.value || "").replace(/\s+/g, "") !== lastRequestedIco)
                return;

            if (!data)
                return;

            // Only set fields that are empty/whitespace
            setIfEmpty(companyElement, data.name);
            setIfEmpty(vatElement, data.vatIdentificationNumber);
            setIfEmpty(taxElement, data.taxIdentificationNumber);
            setIfEmpty(addressElement, data.address);
            setIfEmpty(cityElement, data.city);
            setIfEmpty(postCodeElement, data.postCode);
        };

        input.addEventListener("input", () =>
        {
            // Skip if we set programmatically
            if (silencedInputs.has(input))
                return;

            if (timer)
                clearTimeout(timer);

            timer = setTimeout(handle, 400); // debounce
        });
    },
    bindName: function (input)
    {
        let timer = null;
        let lastQuery = "";
        let requestSeq = 0; // ignore late renders

        const dataSet = input.dataset;

        const orgElement = dataSet.aresOrgSelector
            ? document.querySelector(dataSet.aresOrgSelector)
            : document.getElementById("billing-ico");
        const vatElement = dataSet.aresVatSelector
            ? document.querySelector(dataSet.aresVatSelector)
            : document.getElementById("billing-dic");
        const addressElement = dataSet.aresAddressSelector
            ? document.querySelector(dataSet.aresAddressSelector)
            : document.getElementById("billing-address-1");
        const cityElement = dataSet.aresCitySelector
            ? document.querySelector(dataSet.aresCitySelector)
            : document.getElementById("billing-city");
        const zipElement = dataSet.aresPostcodeSelector
            ? document.querySelector(dataSet.aresPostcodeSelector)
            : document.getElementById("billing-zip");

        const taxElement = dataSet.aresTaxSelector
            ? document.querySelector(dataSet.aresTaxSelector)
            : document.getElementById("billing-icd");

        const countryElement = dataSet.aresCountrySelector
            ? document.querySelector(dataSet.aresCountrySelector)
            : document.getElementById("billing-country");

        // Simple dropdown container
        let dropdown = document.createElement("div");
        dropdown.className = "ares-name-suggestions";
        dropdown.style.position = "absolute";
        dropdown.style.zIndex = "1000";
        dropdown.style.display = "none";
        dropdown.style.background = "#fff";
        dropdown.style.border = "1px solid #ccc";
        dropdown.style.maxHeight = "240px";
        dropdown.style.overflowY = "auto";
        dropdown.style.minWidth = `${input.offsetWidth}px`;

        // Positioning
        const positionDropdown = () =>
        {
            const rect = input.getBoundingClientRect();

            dropdown.style.left = `${rect.left + window.scrollX}px`;
            dropdown.style.top = `${rect.bottom + window.scrollY}px`;
            dropdown.style.minWidth = `${rect.width}px`;
        };

        document.body.appendChild(dropdown);

        positionDropdown();

        window.addEventListener("resize", positionDropdown);
        window.addEventListener("scroll", positionDropdown, true);

        const hide = () =>
        {
            dropdown.style.display = "none";
            dropdown.innerHTML = "";
        };
        const show = () => { dropdown.style.display = "block"; };

        const pick = (item) =>
        {
            // Invalidate any pending render
            requestSeq++;

            // Fill all fields silently (only 'change' event) to avoid re-fetching
            setValue(input, item.name || "", true);

            if (orgElement)
                setValue(orgElement, item.organizationIdentificationNumber || "", true);

            if (vatElement)
                setValue(vatElement, item.vatIdentificationNumber || "", true);

            if (taxElement)
                setValue(taxElement, item.taxIdentificationNumber || "", true);

            if (addressElement)
                setValue(addressElement, item.address || "", true);

            if (cityElement)
                setValue(cityElement, item.city || "", true);

            if (zipElement)
                setValue(zipElement, item.postCode || "", true);

            hide();
        };

        const render = (items) =>
        {
            dropdown.innerHTML = "";

            if (!items || items.length === 0)
            {
                hide();

                return;
            }

            for (const item of items)
            {
                const row = document.createElement("div");
                row.className = "ares-name-suggestion";

                const primary = `${item.name ?? ""}`.trim();
                const secondary = [item.postCode, item.city].filter(Boolean).join(" ");

                row.innerHTML = `<div class="primary">${primary}</div><div class="secondary">${secondary}</div>`;

                row.addEventListener("mousedown", (e) => { e.preventDefault(); }); // Prevent input blur before click
                row.addEventListener("click", () => pick(item));

                dropdown.appendChild(row);
            }

            positionDropdown();
            show();
        };

        const handle = async () =>
        {
            const query = (input.value || "").trim();

            if (query.length < 3)
            {
                hide();

                return;
            }

            lastQuery = query;

            const sequence = ++requestSeq;

            const countryCode = resolveCountryCode(countryElement);
            const items = await fetchAresByNameAsync(query, countryCode);

            // ignore if user changed query since request
            if ((input.value || "").trim() !== lastQuery || sequence !== requestSeq)
                return;

            render(items);
        };

        input.addEventListener("input", () =>
        {
            // Skip if we set programmatically
            if (silencedInputs.has(input))
                return;

            if (timer)
                clearTimeout(timer);

            timer = setTimeout(handle, 300);
        });

        input.addEventListener("blur", () => setTimeout(hide, 150)); // allow click
        input.addEventListener("focus", () =>
        {
            if ((input.value || "").trim().length < 3)
                return;

            handle();
        });
    },
    init: function ()
    {
        var inputs = document.querySelectorAll("input[data-ares='true']");

        for (let input of inputs)
            ares.bind(input);

        inputs = document.querySelectorAll("input[data-ares-name='true']");

        for (let input of inputs)
            ares.bindName(input);
    }
};